import { createContext, useContext, useState, useCallback } from 'react';
import { getBrandingBySlug, getBrandingAtiva } from '../api/empresas';

const BrandingContext = createContext(null);

// Fallback quando nenhuma empresa foi resolvida ainda (ex: slug inválido, erro de rede)
// — mantém a identidade visual da CJC como default da plataforma.
const DEFAULT_BRANDING = {
  nome: 'Portal de Pedidos',
  slug: null,
  cor_primaria: '#070340',
  cor_primaria_hover: '#201D54',
  cor_accent: '#D40018',
  logo_url: null,
};

// Clareia (percent > 0) ou escurece (percent < 0) um hex — usado só para os tons
// decorativos derivados (gradientes de sidebar/avatar/login), nunca para a cor de
// hover configurada pela empresa (essa é literal, vinda de cor_primaria_hover).
function hexShade(hex, percent) {
  const h = (hex || '').replace('#', '');
  if (h.length !== 6) return hex;
  const num = parseInt(h, 16);
  const clamp = (v) => Math.max(0, Math.min(255, v));
  const r = clamp(((num >> 16) & 0xff) + Math.round(255 * percent));
  const g = clamp(((num >> 8) & 0xff) + Math.round(255 * percent));
  const b = clamp((num & 0xff) + Math.round(255 * percent));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

function aplicarCssVars(branding) {
  const root = document.documentElement;
  root.style.setProperty('--cjc-primary', branding.cor_primaria);
  // Hover de botão/tabela é a cor literal escolhida pela empresa — nunca inferida.
  root.style.setProperty('--cjc-primary-hover', branding.cor_primaria_hover);
  // Tons -lt/-dk são só para gradientes decorativos (sidebar, avatar, login) — sempre
  // derivados da cor primária, independente de qual direção a empresa escolheu para o
  // hover (algumas escolhem hover mais claro, outras mais escuro).
  root.style.setProperty('--cjc-primary-lt', hexShade(branding.cor_primaria, 0.20));
  root.style.setProperty('--cjc-primary-dk', hexShade(branding.cor_primaria, -0.35));
  root.style.setProperty('--cjc-accent', branding.cor_accent);
  root.style.setProperty('--cjc-accent-dk', hexShade(branding.cor_accent, -0.20));
  document.title = `${branding.nome} — Portal de Pedidos`;

  let iconLink = document.querySelector('link[rel="icon"]');
  if (branding.favicon_url) {
    if (!iconLink) {
      iconLink = document.createElement('link');
      iconLink.rel = 'icon';
      document.head.appendChild(iconLink);
    }
    iconLink.href = branding.favicon_url;
  } else if (iconLink) {
    iconLink.remove();
  }
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);

  const loadBySlug = useCallback(async (slug) => {
    try {
      const { data } = await getBrandingBySlug(slug);
      setBranding(data);
      aplicarCssVars(data);
      return data;
    } catch {
      setBranding(DEFAULT_BRANDING);
      aplicarCssVars(DEFAULT_BRANDING);
      return null;
    }
  }, []);

  // Usado após login/refresh de sessão — segue a empresa ativa (claim "eoa"),
  // consistente com o isolamento multi-tenant do backend.
  const loadAtiva = useCallback(async () => {
    try {
      const { data } = await getBrandingAtiva();
      if (data) {
        setBranding(data);
        aplicarCssVars(data);
      }
      return data;
    } catch {
      return null;
    }
  }, []);

  // Volta pro branding genérico da plataforma — usado no login SEM slug (nunca deve
  // expor nome/logo de uma empresa específica) e no logout (sessão anterior não pode
  // deixar resíduo visual pro próximo usuário a acessar o mesmo navegador/aba).
  const resetToDefault = useCallback(() => {
    setBranding(DEFAULT_BRANDING);
    aplicarCssVars(DEFAULT_BRANDING);
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, loadBySlug, loadAtiva, resetToDefault }}>
      {children}
    </BrandingContext.Provider>
  );
}

export const useBranding = () => useContext(BrandingContext);
